import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, 
  Users, 
  Calendar, 
  Ticket,
  TrendingUp,
  BarChart3,
  UserCheck,
  ShoppingCart,
  Gavel,
  Heart,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OwnerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }
    
    if (user) {
      verifyOwner();
    }
  }, [user, authLoading, navigate]);

  const verifyOwner = async () => {
    try {
      const response = await axios.get(`${API_URL}/owner/verify`);
      if (response.data.is_owner) {
        setAuthorized(true);
        fetchData();
      } else {
        navigate('/');
      }
    } catch (error) {
      navigate('/');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, transactionsRes, creatorsRes] = await Promise.all([
        axios.get(`${API_URL}/owner/stats`),
        axios.get(`${API_URL}/owner/recent-transactions?limit=100`),
        axios.get(`${API_URL}/owner/creators`)
      ]);
      setStats(statsRes.data);
      setTransactions(transactionsRes.data.transactions);
      setCreators(creatorsRes.data.creators);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!authorized) {
    return null;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Shield className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Owner Dashboard</h1>
              <p className="text-gray-400 text-sm">FanBound Platform Analytics</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-900/40 to-green-800/20 border-green-700/50">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-green-400 text-sm font-medium">Platform Revenue (30%)</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {formatCurrency(stats?.total_platform_revenue)}
                  </p>
                  <p className="text-green-400/70 text-xs mt-1">
                    From {stats?.tickets?.total_sold || 0} tickets + resales
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-blue-700/50">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-blue-400 text-sm font-medium">Total Users</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.users?.total || 0}
                  </p>
                  <p className="text-blue-400/70 text-xs mt-1">
                    {stats?.users?.fans || 0} fans, {stats?.users?.creators || 0} creators
                  </p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-purple-700/50">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-purple-400 text-sm font-medium">Gross Volume</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {formatCurrency(stats?.revenue?.gross_revenue)}
                  </p>
                  <p className="text-purple-400/70 text-xs mt-1">
                    All ticket sales
                  </p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 border-orange-700/50">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-orange-400 text-sm font-medium">Events</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.events?.total || 0}
                  </p>
                  <p className="text-orange-400/70 text-xs mt-1">
                    {stats?.events?.published || 0} live, {stats?.events?.drafts || 0} drafts
                  </p>
                </div>
                <div className="p-3 bg-orange-500/20 rounded-lg">
                  <Calendar className="w-6 h-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 30-Day Summary */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Last 30 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-gray-400 text-sm">New Users</p>
                <p className="text-2xl font-bold text-white">{stats?.last_30_days?.new_users || 0}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Tickets Sold</p>
                <p className="text-2xl font-bold text-white">{stats?.last_30_days?.tickets_sold || 0}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Gross Revenue</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(stats?.last_30_days?.gross_revenue)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Platform Commission</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(stats?.last_30_days?.platform_commission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="creators">Creators</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Detailed Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Tickets */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Ticket className="w-5 h-5 text-pink-500" />
                    Tickets
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Sold</span>
                    <span className="font-semibold">{stats?.tickets?.total_sold || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Active</span>
                    <span className="font-semibold text-green-400">{stats?.tickets?.active || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Used (Scanned)</span>
                    <span className="font-semibold text-gray-500">{stats?.tickets?.used || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Resale Market */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ShoppingCart className="w-5 h-5 text-cyan-500" />
                    Resale Market
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Listings</span>
                    <span className="font-semibold">{stats?.resale?.total_listings || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Active Listings</span>
                    <span className="font-semibold text-yellow-400">{stats?.resale?.active || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sold</span>
                    <span className="font-semibold text-green-400">{stats?.resale?.sold || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Volume</span>
                    <span className="font-semibold">{formatCurrency(stats?.resale?.volume)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Commission</span>
                    <span className="font-semibold text-green-400">{formatCurrency(stats?.resale?.commission)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Auctions */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Gavel className="w-5 h-5 text-amber-500" />
                    Auctions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Bids</span>
                    <span className="font-semibold">{stats?.auctions?.total_bids || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Winning Bids</span>
                    <span className="font-semibold text-green-400">{stats?.auctions?.winning_bids || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Bid Revenue</span>
                    <span className="font-semibold">{formatCurrency(stats?.auctions?.bid_revenue)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Engagement & Top Events */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Engagement */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Heart className="w-5 h-5 text-red-500" />
                    Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Follows</span>
                    <span className="font-semibold">{stats?.engagement?.total_follows || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Team Members</span>
                    <span className="font-semibold">{stats?.engagement?.team_members || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Active Team Members</span>
                    <span className="font-semibold text-green-400">{stats?.engagement?.active_team_members || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Verified Creators</span>
                    <span className="font-semibold text-blue-400">{stats?.users?.verified_creators || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Top Events */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="w-5 h-5 text-indigo-500" />
                    Top Events by Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.top_events?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.top_events.slice(0, 5).map((event, idx) => (
                        <div key={event.event_id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                          <div className="flex items-center gap-3">
                            <span className="text-gray-500 text-sm w-5">{idx + 1}.</span>
                            <div>
                              <p className="font-medium text-sm">{event.event_title}</p>
                              <p className="text-xs text-gray-500">{event.creator_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-green-400">{formatCurrency(event.revenue)}</p>
                            <p className="text-xs text-gray-500">{event.tickets_sold} tickets</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No events yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Revenue Breakdown */}
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Revenue Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Gross Revenue</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(stats?.revenue?.gross_revenue)}</p>
                    <p className="text-xs text-gray-500">Total ticket sales</p>
                  </div>
                  <div className="p-4 bg-green-900/30 rounded-lg border border-green-700/30">
                    <p className="text-green-400 text-sm">Platform (30%)</p>
                    <p className="text-xl font-bold text-green-400">{formatCurrency(stats?.revenue?.platform_commission_30pct)}</p>
                    <p className="text-xs text-green-400/70">Your earnings</p>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Creator Earnings (70%)</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(stats?.revenue?.creator_earnings)}</p>
                    <p className="text-xs text-gray-500">Paid to creators</p>
                  </div>
                  <div className="p-4 bg-cyan-900/30 rounded-lg border border-cyan-700/30">
                    <p className="text-cyan-400 text-sm">Resale Commission</p>
                    <p className="text-xl font-bold text-cyan-400">{formatCurrency(stats?.resale?.commission)}</p>
                    <p className="text-xs text-cyan-400/70">From resale market</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Last 100 ticket purchases</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Event</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Buyer</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Tier</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Price</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, idx) => (
                        <tr key={tx.id || idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                          <td className="py-3 px-4 text-sm text-gray-400">
                            {formatDate(tx.purchased_at)}
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium text-sm">{tx.event_title}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm">{tx.buyer_name}</p>
                            <p className="text-xs text-gray-500">{tx.buyer_email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-xs">
                              {tx.tier_name || 'Standard'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-medium">
                            {formatCurrency(tx.price_paid)}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-green-400">
                            {formatCurrency(tx.commission)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {transactions.length === 0 && (
                    <p className="text-center py-8 text-gray-500">No transactions yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="creators">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle>All Creators</CardTitle>
                <CardDescription>Sorted by revenue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Creator</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Username</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Events</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Followers</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Tickets Sold</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Revenue</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creators.map((creator, idx) => (
                        <tr key={creator.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {creator.avatar_url ? (
                                <img src={creator.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                                  <Users className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-sm">{creator.display_name}</p>
                                <p className="text-xs text-gray-500">{creator.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {creator.username || '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {creator.event_count}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {creator.follower_count}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {creator.total_tickets_sold}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-green-400">
                            {formatCurrency(creator.total_revenue)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {creator.verified ? (
                              <Badge className="bg-blue-500/20 text-blue-400">Verified</Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">No</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {creators.length === 0 && (
                    <p className="text-center py-8 text-gray-500">No creators yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
